import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import type { ExistingMatch, ExistingRecord } from '../types/inbox.types';
import { keys } from './keys';

/** Returns null when the record no longer exists (deleted since the e-mail was analyzed). */
async function fetchExistingMatchRecord(match: ExistingMatch): Promise<ExistingRecord | null> {
  switch (match.kind) {
    case 'event': {
      const { data, error } = await apiClient
        .from('events')
        .select('title, date, all_day, provider_id, provider_name, subject_id, sub_topics(name)')
        .eq('id', match.id)
        .maybeSingle();
      if (error) throw error;
      return data
        ? {
            title: data.title,
            date: data.date,
            allDay: data.all_day,
            providerId: data.provider_id,
            providerName: data.provider_name,
            subTopicId: data.subject_id,
            subTopicName: data.sub_topics?.name ?? null,
          }
        : null;
    }

    case 'pending_item': {
      const { data, error } = await apiClient
        .from('subject_pending_items')
        .select('title, state, provider_id, provider_name, subject_id, sub_topics(name)')
        .eq('id', Number(match.id))
        .maybeSingle();
      if (error) throw error;
      return data
        ? {
            title: data.title,
            providerId: data.provider_id,
            providerName: data.provider_name,
            subTopicId: data.subject_id,
            subTopicName: data.sub_topics?.name ?? null,
            isCompleted: data.state === 'completed',
          }
        : null;
    }

    case 'task': {
      const { data, error } = await apiClient
        .from('subject_tasks')
        .select('title, state, subject_id, sub_topics(name)')
        .eq('id', Number(match.id))
        .maybeSingle();
      if (error) throw error;
      return data
        ? {
            title: data.title,
            subTopicId: data.subject_id,
            subTopicName: data.sub_topics?.name ?? null,
            isCompleted: data.state === 'completed',
          }
        : null;
    }

    case 'contact': {
      const { data, error } = await apiClient
        .from('provider_contacts')
        .select('name, role, email, phone1, provider_id, providers(name)')
        .eq('id', match.id)
        .maybeSingle();
      if (error) throw error;
      return data
        ? {
            title: data.name,
            role: data.role,
            email: data.email,
            phone: data.phone1,
            providerId: data.provider_id,
            providerName: data.providers?.name ?? null,
          }
        : null;
    }
  }
}

type Props = {
  match: ExistingMatch;
};

export function useGetExistingMatchRecord(props: Props) {
  return useQuery({
    queryKey: keys.existingMatch(props.match.kind, props.match.id),
    queryFn: () => fetchExistingMatchRecord(props.match),
  });
}
